<template>
  <div class="scm-back" @click.self="$emit('close')">
    <div class="scm" role="dialog" aria-label="Share a clip">
      <div class="scm-head">
        <h2>Share a clip</h2>
        <button class="scm-x" aria-label="Close" @click="$emit('close')">✕</button>
      </div>

      <div v-if="!clip" class="scm-body">
        <p class="scm-lead">Make a vertical video of <b>{{ title }}</b> with the cover, lyrics and a link back to Muzilly. Post it to Instagram, TikTok or YouTube Shorts.</p>

        <div class="scm-row">
          <span class="scm-lbl">Length</span>
          <div class="scm-chips">
            <button v-for="n in [15, 20, 30]" :key="n" type="button" class="scm-chip" :class="{ on: length === n }" @click="setLength(n)">{{ n }}s</button>
          </div>
        </div>

        <div class="scm-row col">
          <span class="scm-lbl">Starts at <b>{{ fmt(start) }}</b></span>
          <input type="range" class="scm-range" min="0" :max="maxStart" step="1" v-model.number="start" :disabled="busy" aria-label="Clip start" />
          <span class="scm-hint">If the song has synced lyrics, the clip snaps to the nearest line.</span>
        </div>

        <div v-if="busy" class="scm-note"><span class="scm-spin"></span> Making your clip. This can take up to a minute.</div>
        <div v-if="error" class="scm-err">{{ error }}</div>

        <button class="scm-go" :disabled="busy" @click="make">{{ busy ? 'Working…' : 'Make clip' }}</button>
      </div>

      <div v-else class="scm-body">
        <video class="scm-video" :src="clip.url" controls playsinline preload="metadata"></video>
        <div v-if="error" class="scm-err">{{ error }}</div>
        <div class="scm-actions">
          <button class="scm-go" :disabled="sharing" @click="share">{{ sharing ? 'Opening…' : 'Share' }}</button>
          <button class="scm-alt" :disabled="downloading" @click="download">{{ downloading ? 'Saving…' : 'Download' }}</button>
          <button class="scm-alt" @click="copy">{{ copied ? 'Copied' : 'Copy link' }}</button>
        </div>
        <button class="scm-link" @click="clip = null; file = null; blob = null; error = ''">Make a different clip</button>
      </div>
    </div>
  </div>
</template>

<script>
import { functions } from "@/firebase";
import { httpsCallable } from "firebase/functions";

export default {
  name: "ShareClipModal",
  // Move the modal to <body> so a parent with a transform (like the full-screen player) can't trap it.
  mounted() { try { document.body.appendChild(this.$el); } catch (e) { /* ignore */ } },
  beforeDestroy() { try { if (this.$el && this.$el.parentNode) this.$el.parentNode.removeChild(this.$el); } catch (e) { /* ignore */ } },
  props: {
    trackId: { type: String, required: true },
    title: { type: String, default: "" },
    duration: { type: Number, default: 0 },   // seconds, 0 when unknown
  },
  data() {
    const length = 20;
    const dur = this.duration || 0;
    return { length, start: dur ? Math.max(0, Math.min(Math.round(dur * 0.3), Math.floor(dur - length))) : 0, busy: false, error: "", clip: null, file: null, blob: null, sharing: false, downloading: false, copied: false };
  },
  computed: {
    maxStart() { return this.duration ? Math.max(0, Math.floor(this.duration - this.length)) : 180; },
  },
  methods: {
    fmt(s) { s = Math.max(0, Math.round(s)); return Math.floor(s / 60) + ":" + String(s % 60).padStart(2, "0"); },
    setLength(n) { this.length = n; if (this.start > this.maxStart) this.start = this.maxStart; },
    async make() {
      this.busy = true; this.error = "";
      try {
        const r = await httpsCallable(functions, "createShareClip", { timeout: 300000 })({ trackId: this.trackId, start: this.start, length: this.length });
        this.clip = r.data;
        this.preload();
      } catch (e) {
        this.error = (e && e.message) || "We couldn't make that clip. Please try again.";
      }
      this.busy = false;
    },
    shareText() { return `${this.clip.title} by ${this.clip.artist} on Muzilly\n${this.clip.link}`; },
    // Fetch the video ahead of time: browsers only allow sharing straight from a tap, so it can't wait on a download.
    async preload() {
      this.file = null;
      try {
        const res = await fetch(this.clip.url);
        if (!res.ok) return;
        const blob = await res.blob();
        this.blob = blob;
        this.file = new File([blob], "muzilly-clip.mp4", { type: "video/mp4" });
      } catch (e) { /* the browser blocked reading it; Download still works */ }
    },
    async share() {
      this.error = "";
      try {
        if (this.file && navigator.canShare && navigator.canShare({ files: [this.file] })) {
          this.sharing = true;
          await navigator.share({ files: [this.file], text: this.shareText() });
        } else if (this.blob) {
          this.save(this.blob);
          this.error = "Your browser can't share video directly, so the clip was downloaded. Post it from your Downloads.";
        } else if (navigator.share) {
          this.sharing = true;
          await navigator.share({ title: this.clip.title, text: this.shareText(), url: this.clip.link });
        } else {
          this.error = "Use Download, then post the file. Copy link works for the link.";
        }
      } catch (e) {
        if (!(e && e.name === "AbortError")) this.error = "Couldn't open sharing. Use Download, then post the file.";
      }
      this.sharing = false;
    },
    async download() {
      this.error = ""; this.downloading = true;
      try {
        let blob = this.blob;
        if (!blob) {
          const res = await fetch(this.clip.url);
          if (!res.ok) throw new Error("download failed");
          blob = await res.blob();
          this.blob = blob;
        }
        this.save(blob);
      } catch (e) {
        // Reading the file was blocked; the clip link itself downloads without leaving this page.
        const a = document.createElement("a");
        a.href = this.clip.url; a.download = "muzilly-clip.mp4";
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
      }
      this.downloading = false;
    },
    save(blob) {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "muzilly-clip.mp4";
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 4000);
    },
    async copy() {
      try { await navigator.clipboard.writeText(this.clip.link); this.copied = true; setTimeout(() => { this.copied = false; }, 1800); }
      catch (e) { this.error = "Couldn't copy the link."; }
    },
  },
};
</script>

<style scoped>
.scm-back{position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,.7);z-index:2147483000;display:flex;align-items:center;justify-content:center;padding:16px}
.scm{background:#15121a;border:0.5px solid rgba(245,240,232,.12);border-radius:16px;width:100%;max-width:440px;max-height:92vh;max-height:92dvh;overflow-y:auto;-webkit-overflow-scrolling:touch;color:#f5f0e8;font-family:'DM Sans','Helvetica Neue',sans-serif}
.scm-head{display:flex;align-items:center;justify-content:space-between;padding:16px 20px;border-bottom:0.5px solid rgba(245,240,232,.08)}
.scm-head h2{font-size:17px;font-weight:600;margin:0}
.scm-x{background:none;border:none;color:rgba(245,240,232,.5);font-size:16px;cursor:pointer;padding:4px 8px}
.scm-body{padding:18px 20px 20px;display:flex;flex-direction:column;gap:16px}
.scm-lead{margin:0;font-size:14px;line-height:1.5;color:rgba(245,240,232,.75)}
.scm-row{display:flex;align-items:center;justify-content:space-between;gap:12px}
.scm-row.col{flex-direction:column;align-items:stretch;gap:8px}
.scm-lbl{font-size:13px;color:rgba(245,240,232,.7)}
.scm-lbl b{color:#f5f0e8}
.scm-chips{display:flex;gap:8px}
.scm-chip{padding:7px 14px;border-radius:999px;border:0.5px solid rgba(245,240,232,.25);background:none;color:#f5f0e8;font-family:inherit;font-size:13px;cursor:pointer}
.scm-chip.on{background:#d4af6e;border-color:#d4af6e;color:#15121a;font-weight:600}
.scm-range{width:100%;accent-color:#d4af6e}
.scm-hint{font-size:12px;color:rgba(245,240,232,.45)}
.scm-note{display:flex;align-items:center;gap:8px;font-size:13px;color:rgba(245,240,232,.75)}
.scm-err{font-size:13px;color:#e07a7a}
.scm-spin{width:14px;height:14px;border-radius:50%;border:2px solid rgba(245,240,232,.25);border-top-color:#d4af6e;animation:scmspin 1s linear infinite;flex-shrink:0}
@keyframes scmspin{to{transform:rotate(360deg)}}
.scm-go{padding:12px 18px;border-radius:999px;border:none;background:#d4af6e;color:#15121a;font-family:inherit;font-size:14px;font-weight:600;cursor:pointer;text-align:center}
.scm-go:disabled{opacity:.55;cursor:default}
.scm-video{width:100%;max-height:50vh;border-radius:12px;background:#000;aspect-ratio:9/16;object-fit:contain}
.scm-actions{display:flex;gap:10px;flex-wrap:wrap;align-items:center}
.scm-actions .scm-go{flex:1;min-width:120px}
.scm-alt{padding:11px 16px;border-radius:999px;border:0.5px solid rgba(245,240,232,.3);background:none;color:#f5f0e8;font-family:inherit;font-size:13px;cursor:pointer;text-decoration:none;text-align:center}
.scm-link{background:none;border:none;color:rgba(245,240,232,.55);font-family:inherit;font-size:13px;cursor:pointer;text-decoration:underline;align-self:center}
</style>